// One destination, several bookings: the rules for a VISIT.
//
// A customer who orders twice has two SOs, and sales books both for one day.
// One truck stops once, so the day should give up one seat, not two. A booking
// joins a visit through `visitId` -- the id of the booking the visit started
// from -- and the day keeps a mirror, `bookingDay.visits: { <visitId>: n }`,
// counting the visit's active members on that day. The seat moves only on the
// edges: the first member takes it, and the last to leave gives it back.
//
// The mirror exists for the reason tripByProvince does: a Firestore transaction
// cannot run a query, so "is someone of this visit already booked here" has to
// live on the one document the transaction reads anyway.
//
// Pure, CommonJS, asserted by npm run test:rules, like bookingRules. Design:
// docs/superpowers/specs/2026-09-28-same-visit-design.md.
const R = require('./bookingRules')

// Phone AND ตำบล. The phone alone is what the driver app groups by, and it is
// not enough here: a branch switchboard or a shop's own number typed for a
// walk-in is shared by customers who have nothing to do with each other, and a
// merge gives away a seat. No phone or no ตำบล is no key -- a name is not an
// identity.
function visitKey (b) {
  const x = b || {}
  const tel = R.normalizeTel(x.telephone || x.custTel)
  const loc = String(x.location || '').trim()
  if (!tel || !loc) return null
  return tel + '|' + loc
}

function membersOn (dayDoc, visitId) {
  const v = (dayDoc && dayDoc.visits) || {}
  return Math.max(0, Math.floor(Number(v[visitId])) || 0)
}

// A booking arriving on a day. `visitId` null is an ordinary booking and always
// takes a seat. -> { takesSeat, visits }  where `visits` is the one-key patch
// for the day document (a set with merge deep-merges maps, so writing the one
// key is safe) or null.
function join (dayDoc, visitId) {
  if (!visitId) return { takesSeat: true, visits: null }
  const n = membersOn(dayDoc, visitId)
  return { takesSeat: n === 0, visits: { [visitId]: n + 1 } }
}

// And leaving it: cancelled, moved away, or unlinked. A member whose mirror
// entry is missing (written before this existed, or drifted) counts as the
// last one out and gives the seat back -- the safe side, since the recount
// rebuilds the mirror and a seat held by nobody blocks a day for real.
function leave (dayDoc, booking) {
  const vid = booking && booking.visitId
  if (!vid) return { releasesSeat: true, visits: null }
  const next = Math.max(0, membersOn(dayDoc, vid) - 1)
  return { releasesSeat: next === 0, visits: { [vid]: next } }
}

// The visit a booking would join with `leader`: the leader's own visit if it has
// one, else a new one named after the leader.
function visitIdFor (leader, leaderId) {
  return (leader && leader.visitId) || leaderId || null
}

// May `b` join `leader`'s visit? Both are bookingSlot documents; `b` may be a
// booking not yet written (createBooking), in which case `bId` is null.
//
// Same date and SLOT, not only the same day: the visit holds one seat, and a
// seat is counted against a slot. เช้า + บ่าย for one customer are two trips the
// customer asked for -- move one first if they should be one.
// Same route when out of area: the seat is the route's, and a booking with no
// route yet (a stray from before routes existed) has no seat anyone could share.
//
// -> { ok: true, visitId } | { ok: false, reason }
function checkLink (b, bId, leader, leaderId) {
  if (!b || !leader) return { ok: false, reason: 'NOT_FOUND' }
  if (bId && bId === leaderId) return { ok: false, reason: 'VISIT_SELF' }
  if (!R.isActive(b.status || 'active') || !R.isActive(leader.status)) return { ok: false, reason: 'ALREADY_CANCELLED' }
  if (b.visitId) return { ok: false, reason: 'VISIT_LINKED' }
  if (isPending(b) || isPending(leader)) return { ok: false, reason: 'PROPOSAL_PENDING' }
  if (b.date !== leader.date || b.slot !== leader.slot) return { ok: false, reason: 'VISIT_SLOT' }
  const key = visitKey(b)
  if (!key || key !== visitKey(leader)) return { ok: false, reason: 'VISIT_KEY' }
  if (!!b.outOfArea !== !!leader.outOfArea) return { ok: false, reason: 'VISIT_KEY' }
  if (b.outOfArea && (!b.tripId || b.tripId !== leader.tripId)) return { ok: false, reason: 'VISIT_TRIP' }
  return { ok: true, visitId: visitIdFor(leader, leaderId) }
}

// The booking's own `proposal` shape (proposalRules.isPending), restated rather
// than required: proposalRules requires people.js, and this module must stay
// loadable wherever bookingRules is.
function isPending (b) {
  return !!(b && b.proposal && b.proposalStatus === 'pending')
}

// Candidates the page offers to link `b` with: live bookings on the same date
// and slot with the same key, not `b` itself. A candidate already in a visit is
// offered once, as that visit. -> [booking with id]
function linkCandidates (b, rows) {
  if (!b || b.visitId || !R.isActive(b.status)) return []
  const seen = {}
  return (rows || []).filter(o => {
    if (!o || o.id === b.id || !R.isActive(o.status)) return false
    if (!checkLink(b, b.id, o, o.id).ok) return false
    const vid = visitIdFor(o, o.id)
    if (seen[vid]) return false
    seen[vid] = true
    return true
  })
}

// Every other active member of `b`'s visit in `rows`, for the row that names
// who it shares a stop with.
function mates (b, rows) {
  if (!b || !b.visitId) return []
  return (rows || []).filter(o => o && o.id !== b.id && o.visitId === b.visitId &&
    o.date === b.date && R.isActive(o.status))
}

// Distinct stops among `rows` -- one per visit, one per unlinked booking. What
// the day line reads as "N จุดส่ง".
function stopCount (rows) {
  const seen = {}
  let n = 0
  ;(rows || []).forEach(b => {
    if (!b || !R.isActive(b.status)) return
    if (!b.visitId) { n++; return }
    const k = b.date + '|' + b.visitId
    if (!seen[k]) { seen[k] = true; n++ }
  })
  return n
}

// The recount's view of one day: which entries take a seat, and the mirror.
// `entries` are countsForRecount's own-date entries, each with its booking's
// visitId. -> { seated: [entry], visits: { vid: n } }
function recountDay (entries) {
  const visits = {}
  const seated = []
  ;(entries || []).forEach(e => {
    if (!e.visitId) { seated.push(e); return }
    visits[e.visitId] = (visits[e.visitId] || 0) + 1
    if (visits[e.visitId] === 1) seated.push(e)
  })
  return { seated, visits }
}

// A net seat change per route, for the paths that move a booking between
// routes AND may or may not hold a seat of its own on the way out. Keeps the
// rule "read and write a route only when its count changes" in one place: the
// Web SDK v1 rejects a transaction that reads a document it does not write.
// -> { [tripId]: delta } with zero entries dropped.
function tripDeltas (changes) {
  const out = {}
  ;(changes || []).forEach(c => {
    if (!c || !c.tripId || !c.delta) return
    out[c.tripId] = (out[c.tripId] || 0) + c.delta
  })
  Object.keys(out).forEach(k => { if (!out[k]) delete out[k] })
  return out
}

module.exports = {
  visitKey, membersOn, join, leave, visitIdFor, checkLink, linkCandidates,
  mates, stopCount, recountDay, tripDeltas
}
